import { useMemo, useState } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Container, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import PageBanner from '../../components/Common/PageBanner';
import SectionHeading from '../../components/Common/SectionHeading';
import { companyInfo, pageActions, pageContent, productSelectOptions } from '../../data/siteData';

const contactFormName = 'contact-us';

function ContactPage() {
  const content = pageContent.contactPage;
  const initialForm = useMemo(
    () => ({
      fullName: '',
      email: '',
      phone: '',
      city: '',
      range: '',
      message: '',
    }),
    []
  );
  const [formData, setFormData] = useState(initialForm);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <PageBanner {...content.banner} />
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <SectionHeading
          eyebrow={content.heading.eyebrow}
          title={content.heading.title}
          subtitle={content.heading.subtitle}
          align="left"
        />
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Stack spacing={2.2}>
              {content.infoCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Paper key={card.label} elevation={0} sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', borderRadius: 3.5, bgcolor: '#fff0c8', color: '#c9a84c' }}>
                        <Icon />
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#4a0e8f', fontWeight: 800 }}>{card.label}</Typography>
                        <Typography sx={{ color: 'text.secondary' }}>{companyInfo[card.valueKey]}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#1a0533', color: '#ffffff' }}>
                <Typography variant="h4" sx={{ color: '#ffffff', mb: 1 }}>{content.whatsappBox.title}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, mb: 2 }}>
                  {content.whatsappBox.description}
                </Typography>
                <Button component="a" href={pageActions.whatsappPath} target="_blank" rel="noreferrer" startIcon={<WhatsAppIcon />} variant="contained" color="primary">
                  {content.whatsappBox.buttonLabel}
                </Button>
              </Paper>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 } }}>
              <Box
                component="form"
                name={contactFormName}
                method="POST"
                action="/thank-you.html"
                data-netlify="true"
                netlify-honeypot="bot-field"
              >
                <input type="hidden" name="form-name" value={contactFormName} />
                <Box sx={{ display: 'none' }}>
                  <label htmlFor="contact-bot-field">
                    Don&apos;t fill this out if you&apos;re human:
                    <input id="contact-bot-field" name="bot-field" />
                  </label>
                </Box>
                <Grid container spacing={2}>
                  {content.form.fields
                    .filter((field) => field.name !== 'message')
                    .map((field) => (
                      <Grid item xs={12} sm={6} key={field.name}>
                        <TextField
                          fullWidth
                          name={field.name}
                          label={field.label}
                          type={field.type || 'text'}
                          required={field.required}
                          value={formData[field.name]}
                          onChange={handleChange}
                        />
                      </Grid>
                    ))}
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="range"
                      label={content.form.rangeLabel}
                      value={formData.range}
                      onChange={handleChange}
                    >
                      {productSelectOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {content.form.fields
                    .filter((field) => field.name === 'message')
                    .map((field) => (
                      <Grid item xs={12} key={field.name}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={field.rows}
                          name={field.name}
                          label={field.label}
                          value={formData[field.name]}
                          onChange={handleChange}
                        />
                      </Grid>
                    ))}
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                      {content.form.submitLabel}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ContactPage;
